'use client';

import { useCallback, useEffect } from 'react';
import { useAuthenticationContext } from '@/context/authenticationProvider';
import { MixpanelTracking } from '@/services/mixpanel';
import { useAgentContext } from '../context/AgentContext';
import type {
  WebSocketEvent,
  ConversationCreatedEvent,
  TokenEvent,
  ToolCallEvent,
  ToolResultEvent,
  DoneEvent,
  ErrorEvent,
  UsageEvent,
  WebSocketSendMessage,
  WebSocketCancelMessage,
  PageContext,
} from '../types/agent.types';

const AGENT_WS_URL = process.env.NEXT_PUBLIC_AGENT_WS_URL;
const RECONNECT_DELAY = 3000;

// Module-level singleton — shared across all hook consumers.
// This ensures connect() in AgentPanel and sendMessage() in ChatInput
// operate on the same WebSocket instance.
let wsInstance: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;
let usageData: UsageEvent | null = null;
let pendingMessage: { payload: WebSocketSendMessage; dispatch: () => void } | null = null;
let streamedResponseContent = '';
let lastUserPrompt = '';

// Store the latest dispatch/setters so the WS message handler
// can call them without stale closures.
let latestDispatch: ReturnType<typeof useAgentContext>['dispatch'] | null = null;
let latestConnect: (() => Promise<void>) | null = null;
let latestSetConnectionStatus: ReturnType<typeof useAgentContext>['setConnectionStatus'] | null =
  null;
let latestSetActiveConversation:
  | ReturnType<typeof useAgentContext>['setActiveConversation']
  | null = null;

function cleanupWs() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  pendingMessage = null;
  if (wsInstance) {
    wsInstance.onopen = null;
    wsInstance.onmessage = null;
    wsInstance.onerror = null;
    wsInstance.onclose = null;
    wsInstance.close();
    wsInstance = null;
  }
}

function handleWsMessage(event: MessageEvent) {
  try {
    const wsEvent: WebSocketEvent = JSON.parse(event.data);
    const { type, data } = wsEvent;

    if (!latestDispatch || !latestSetActiveConversation) return;

    switch (type) {
      case 'conversation_created': {
        const { conversationId } = data as ConversationCreatedEvent;
        latestSetActiveConversation(conversationId);
        try {
          localStorage.setItem('sw-ai-last-conversation', conversationId);
        } catch {
          /* ignore */
        }
        break;
      }
      case 'token': {
        const { text } = data as TokenEvent;
        streamedResponseContent += text;
        latestDispatch({ type: 'APPEND_TOKEN', payload: text });
        break;
      }
      case 'tool_call': {
        const toolCall = data as ToolCallEvent;
        latestDispatch({
          type: 'ADD_TOOL_CALL',
          payload: {
            toolUseId: toolCall.toolUseId,
            toolName: toolCall.toolName,
            input: toolCall.input,
            status: 'pending',
          },
        });
        break;
      }
      case 'tool_result': {
        const toolResult = data as ToolResultEvent;
        latestDispatch({
          type: 'UPDATE_TOOL_CALL',
          payload: {
            toolUseId: toolResult.toolUseId,
            status: toolResult.status,
            result: toolResult.result,
          },
        });
        break;
      }
      case 'usage': {
        usageData = data as UsageEvent;
        break;
      }
      case 'done': {
        const doneData = data as DoneEvent;
        latestDispatch({
          type: 'FINISH_STREAMING',
          payload: {
            messageId: doneData.messageId,
            tokenUsage: usageData || undefined,
          },
        });

        MixpanelTracking.getInstance().track('ai_assistant_response', {
          prompt: lastUserPrompt,
          response: streamedResponseContent.slice(0, 2000),
          messageId: doneData.messageId,
          tokenUsage: usageData
            ? {
                input: usageData.inputTokens,
                output: usageData.outputTokens,
              }
            : null,
        });
        streamedResponseContent = '';
        usageData = null;
        break;
      }
      case 'error': {
        const errorData = data as ErrorEvent;
        latestDispatch({ type: 'SET_ERROR', payload: errorData.message });
        break;
      }
      case 'cancelled': {
        latestDispatch({
          type: 'FINISH_STREAMING',
          payload: {
            tokenUsage: usageData || undefined,
          },
        });
        usageData = null;
        break;
      }
    }
  } catch {
    console.error('Failed to parse WebSocket message');
  }
}

export function useAgentWebSocket() {
  const { getAuthenticatedToken } = useAuthenticationContext();
  const { state, dispatch, setConnectionStatus, setActiveConversation } = useAgentContext();

  // Keep module-level refs in sync with latest context values
  latestDispatch = dispatch;
  latestSetConnectionStatus = setConnectionStatus;
  latestSetActiveConversation = setActiveConversation;

  const connect = useCallback(async () => {
    if (!AGENT_WS_URL) {
      console.warn('NEXT_PUBLIC_AGENT_WS_URL not configured');
      return;
    }

    if (
      wsInstance?.readyState === WebSocket.OPEN ||
      wsInstance?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    cleanupWs();
    intentionalClose = false;
    setConnectionStatus('connecting');

    try {
      const token = await getAuthenticatedToken();
      if (!token) {
        setConnectionStatus('error');
        return;
      }

      const ws = new WebSocket(`${AGENT_WS_URL}?token=${token}`);
      wsInstance = ws;

      ws.onopen = () => {
        latestSetConnectionStatus?.('connected');
        // Flush any message queued while connecting
        if (pendingMessage && ws.readyState === WebSocket.OPEN) {
          pendingMessage.dispatch();
          ws.send(JSON.stringify(pendingMessage.payload));
          pendingMessage = null;
        }
      };

      ws.onmessage = handleWsMessage;

      ws.onerror = () => {
        latestSetConnectionStatus?.('error');
      };

      ws.onclose = () => {
        latestSetConnectionStatus?.('disconnected');
        wsInstance = null;

        // Auto-reconnect on unexpected close
        if (!intentionalClose) {
          reconnectTimer = setTimeout(() => {
            latestConnect?.();
          }, RECONNECT_DELAY);
        }
      };
    } catch {
      setConnectionStatus('error');
    }
  }, [getAuthenticatedToken, setConnectionStatus]);

  // Keep module-level ref in sync so reconnect timer can call connect()
  latestConnect = connect;

  const disconnect = useCallback(() => {
    intentionalClose = true;
    cleanupWs();
    setConnectionStatus('disconnected');
  }, [setConnectionStatus]);

  const sendMessage = useCallback(
    (message: string, conversationId?: string, context?: PageContext) => {
      const payload: WebSocketSendMessage = {
        action: 'chat',
        message,
        ...(conversationId && { conversationId }),
        ...(context && { context }),
      };

      if (wsInstance?.readyState === WebSocket.OPEN) {
        lastUserPrompt = message;
        streamedResponseContent = '';
        dispatch({ type: 'START_STREAMING' });
        wsInstance.send(JSON.stringify(payload));
      } else if (wsInstance?.readyState === WebSocket.CONNECTING) {
        // Queue message to send once connected
        pendingMessage = {
          payload,
          dispatch: () => latestDispatch?.({ type: 'START_STREAMING' }),
        };
      }
    },
    [dispatch]
  );

  const cancelResponse = useCallback((conversationId: string) => {
    if (wsInstance?.readyState !== WebSocket.OPEN) return;

    const payload: WebSocketCancelMessage = {
      action: 'cancel',
      conversationId,
    };

    wsInstance.send(JSON.stringify(payload));
  }, []);

  // Cleanup on unmount (only matters for the component that manages lifecycle)
  useEffect(() => {
    return () => {
      // Don't cleanup on unmount by default — the singleton persists
      // Only cleanup when intentionally disconnecting via disconnect()
    };
  }, []);

  return {
    connect,
    disconnect,
    sendMessage,
    cancelResponse,
    isConnected: state.connectionStatus === 'connected',
  };
}
